import {
  ArrowRight,
  CalendarCheck,
  CheckCircle2,
  FileCheck,
  FileText,
  History,
  Info,
  RotateCcw,
  Send,
  FileSignature,
  Users,
} from 'lucide-react'
import type { Deal, DealStage } from '@/types'
import { getMissingBantFields, stageProgression } from './utils'

export type PanelTab = 'info' | 'proposal' | 'contract' | 'history'

export type PrimaryDecision = {
  label?: string
  description: string
  icon?: React.ReactNode
  className?: string
  targetStage?: DealStage
  scrollTargetId?: string
}

/** Largura do painel: tela cheia no mobile, metade da viewport no desktop. */
export const DEAL_DETAIL_PANEL_MAX_WIDTH = 'md:w-1/2'

export const STAGE_SECTION_IDS = {
  agendarReuniao: 'agendar-reuniao-section',
  reuniaoAgendada: 'reuniao-agendada-section',
  reuniaoRealizada: 'reuniao-realizada-section',
} as const

export const panelTabs: { id: PanelTab; label: string; icon: React.ElementType }[] = [
  { id: 'info', label: 'Dados', icon: Info },
  { id: 'proposal', label: 'Proposta', icon: FileText },
  { id: 'contract', label: 'Contrato', icon: FileSignature },
  { id: 'history', label: 'Histórico', icon: History },
]

export const progressStages: DealStage[] = [
  'qualificado',
  'reuniao_agendada',
  'reuniao_realizada',
  'proposta_elaboracao',
  'proposta_enviada',
  'teste_gratis',
  'renegociacao',
  'ajustar_proposta',
  'gerar_contrato',
  'contrato_enviado',
  'handover',
  'ganho',
]

export const decisionCopy: Partial<Record<DealStage, PrimaryDecision>> = {
  qualificado: {
    label: 'Agendar reunião',
    description: 'Defina data e hora na seção abaixo para avançar para Reunião Agendada.',
    icon: <CalendarCheck size={13} />,
    scrollTargetId: STAGE_SECTION_IDS.agendarReuniao,
  },
  reuniao_agendada: {
    label: 'Confirmar reunião',
    description: 'Preencha a observação em amarelo e confirme a reunião.',
    icon: <CheckCircle2 size={13} />,
    className:
      'bg-amber-400 font-semibold text-zinc-900 hover:bg-amber-300 hover:text-zinc-900',
  },
  reuniao_realizada: {
    label: 'Iniciar proposta',
    description: 'Complete o BANT e avance para proposta em elaboração.',
    icon: <ArrowRight size={13} />,
    scrollTargetId: STAGE_SECTION_IDS.reuniaoRealizada,
  },
  proposta_elaboracao: {
    label: 'Marcar proposta enviada',
    description: 'Use quando a proposta estiver pronta e enviada ao cliente.',
    icon: <Send size={13} />,
    targetStage: 'proposta_enviada',
  },
  proposta_enviada: {
    label: 'Escolher próximo passo',
    description: 'Gerar contrato, 30 dias grátis, renegociar ou registrar perda.',
    icon: <FileCheck size={13} />,
  },
  teste_gratis: {
    label: 'Gerar contrato',
    description: 'O teste acabou ou o cliente decidiu seguir. Formalize o contrato. O lembrete de 30 dias fica nos Blocos.',
    icon: <FileCheck size={13} />,
    targetStage: 'gerar_contrato',
  },
  renegociacao: {
    label: 'Reenviar proposta',
    description: 'Ajuste o plano na aba Proposta e reenvie quando estiver pronta.',
    icon: <Send size={13} />,
    targetStage: 'proposta_enviada',
  },
  ajustar_proposta: {
    label: 'Proposta ajustada',
    description: 'Volte para Gerar Contrato quando o financeiro puder seguir com o documento.',
    icon: <FileCheck size={13} />,
    targetStage: 'gerar_contrato',
  },
  gerar_contrato: {
    label: 'Contrato enviado',
    description:
      'O financeiro elabora e envia o contrato. O deal avança automaticamente quando o chamado for marcado como enviado.',
    icon: <FileCheck size={13} />,
    targetStage: 'contrato_enviado',
  },
  contrato_enviado: {
    label: 'Marcar Handover',
    description: 'Avance após o contrato assinado para preparar a passagem ao pós-venda.',
    icon: <Users size={13} />,
    targetStage: 'handover',
  },
  handover: {
    label: 'Handover concluído',
    description:
      'O deal já está ganho nesta etapa. Confirme os dados do contrato e a observação do projeto.',
    icon: <CheckCircle2 size={13} />,
    className: 'bg-emerald-600 hover:bg-emerald-500',
    targetStage: 'ganho',
  },
  ganho: { description: 'Deal concluído como contrato assinado.' },
  perdido: {
    label: 'Voltar para renegociação',
    description: 'Reabra o deal em renegociação; o motivo da perda permanece no histórico.',
    icon: <RotateCcw size={13} />,
    targetStage: 'renegociacao',
  },
  pausado: { description: 'Deal pausado. Reative quando houver novo contexto comercial.' },
  fechamento_futuro: { description: 'Deal fora do pipeline ativo até a data de retomada.' },
}

export function getDefaultPanelTab(etapa?: DealStage | null): PanelTab {
  if (etapa === 'gerar_contrato' || etapa === 'contrato_enviado' || etapa === 'handover') return 'contract'
  if (etapa === 'proposta_elaboracao' || etapa === 'renegociacao' || etapa === 'ajustar_proposta') return 'proposal'
  return 'info'
}

export function getMissingRequirements(deal: Deal, targetStage?: DealStage) {
  if (deal.etapa === 'reuniao_realizada' && targetStage === 'proposta_elaboracao') {
    return getMissingBantFields(deal)
  }
  return []
}

/** @deprecated Prefer getStageStepper for UI */
export function getStageProgress(deal: Deal) {
  const stepper = getStageStepper(deal)
  if (stepper.kind !== 'active') return 0
  return Math.round((stepper.index / (stepper.total - 1)) * 100)
}

export type StageStepperState =
  | { kind: 'active'; current: number; total: number; index: number }
  | { kind: 'terminal'; label: string }
  | { kind: 'unknown' }

const terminalLabels: Partial<Record<DealStage, string>> = {
  ganho: 'Deal ganho — pipeline concluído',
  perdido: 'Deal perdido',
  pausado: 'Deal pausado',
  fechamento_futuro: 'Agendado para retomada futura',
}

export function getStageStepper(deal: Deal): StageStepperState {
  const terminal = terminalLabels[deal.etapa]
  if (terminal) return { kind: 'terminal', label: terminal }

  const index = progressStages.indexOf(deal.etapa)
  if (index < 0) return { kind: 'unknown' }

  return {
    kind: 'active',
    current: index + 1,
    total: progressStages.length,
    index,
  }
}

export function resolvePrimaryDecision(deal: Deal): PrimaryDecision {
  return (
    decisionCopy[deal.etapa] ?? {
      label: 'Avançar etapa',
      description: 'Avance para a próxima etapa configurada no pipeline.',
      icon: <ArrowRight size={13} />,
      targetStage: stageProgression[deal.etapa],
    }
  )
}

export function scrollToDealSection(sectionId: string, container?: HTMLElement | null) {
  const root = container ?? document
  const el = root.querySelector(`#${CSS.escape(sectionId)}`)
  el?.scrollIntoView({ behavior: 'smooth', block: 'start' })
}

export function resolveFooterPrimary(deal: Deal, decision: PrimaryDecision) {
  if (deal.etapa === 'reuniao_agendada' && decision.label) {
    return {
      label: decision.label,
      icon: decision.icon,
      className: decision.className,
      mode: 'confirm' as const,
    }
  }

  if (decision.scrollTargetId && decision.label) {
    return {
      label: decision.label,
      icon: decision.icon,
      className: decision.className,
      mode: 'scroll' as const,
      scrollTargetId: decision.scrollTargetId,
    }
  }

  if (decision.label && (decision.targetStage || deal.etapa === 'proposta_enviada')) {
    return {
      label: decision.label,
      icon: decision.icon,
      className: decision.className,
      mode: 'action' as const,
    }
  }

  return null
}
